import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { accessOf } from '../../utils/access';
import { roleLabel, sectionsFor } from '../../utils/staff';
import BrandMark from '../common/BrandMark';
import styles from './Sidebar.module.css';

interface SidebarProps {
  context: 'dashboard' | 'project';
  projectId?: string;
  open?: boolean;
  onClose?: () => void;
}

interface NavItem {
  to: string;
  icon: string;
  label: string;
  mainOnly?: boolean;
}

const dashboardNavItems: NavItem[] = [
  { to: '/dashboard', icon: 'grid_view', label: 'Dashboard' },
  { to: '/projects', icon: 'folder_managed', label: 'Projects' },
  // A subuser's team is managed by its main user.
  { to: '/team', icon: 'groups', label: 'Team Management', mainOnly: true },
  { to: '/sharing', icon: 'share_reviews', label: 'Sharing Center' },
  { to: '/activity', icon: 'receipt_long', label: 'Activity Logs' },
  { to: '/trash', icon: 'delete', label: 'Trash' },
];

const getEnvLabel = (): string | null => {
  if (import.meta.env.DEV) return 'LOCAL';
  if (import.meta.env.VITE_APP_ENV === 'qa') return 'QA';
  return null;
};

const Sidebar = ({ context, projectId, open = false, onClose }: SidebarProps) => {
  const { user, logout, isStaff } = useAuth();
  const access = accessOf(user);
  const navigate = useNavigate();
  const envLabel = getEnvLabel();

  const projectNavItems: NavItem[] = [
    { to: `/projects/${projectId}`, icon: 'dashboard', label: 'Project Overview' },
    { to: `/projects/${projectId}/2d`, icon: 'architecture', label: '2D Drawings' },
    { to: `/projects/${projectId}/3d`, icon: 'view_in_ar', label: '3D BIM Models' },
    { to: `/projects/${projectId}/documents`, icon: 'description', label: 'Documents' },
    // Gaussian Splats replaces the retired Indoor Viewer slot — permission-gated.
    ...(access.splat
      ? [{ to: `/projects/${projectId}/gaussian`, icon: 'blur_on', label: 'Gaussian Splats' }]
      : []),
    ...(access.ortho
      ? [{ to: `/projects/${projectId}/orthomosaic`, icon: 'satellite_alt', label: 'Orthomosaic' }]
      : []),
    ...(access.video360
      ? [{ to: `/projects/${projectId}/video360`, icon: 'panorama_photosphere', label: '360 Videos' }]
      : []),
    { to: `/projects/${projectId}/point-cloud`, icon: 'grain', label: 'Point Cloud' },
  ];

  const navItems = context === 'dashboard'
    ? dashboardNavItems.filter((i) => !i.mainOnly || !user?.isSubuser)
    : projectNavItems;
  const adminNavItems = [
    { to: '/admin', icon: 'admin_panel_settings', label: 'Admin Dashboard' },
    ...sectionsFor(user).map((s) => ({ to: s.to, icon: s.icon, label: s.title })),
  ];

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <>
      {open && <div className={styles.backdrop} onClick={onClose} />}
      <aside className={open ? `${styles.sidebar} ${styles.sidebarOpen}` : styles.sidebar}>
        <div className={styles.logoSection}>
          <div className="flex items-center gap-3">
            <div className={styles.logoIcon}>
              <BrandMark size={40} decorative />
            </div>
            <div>
              <div className="flex items-center gap-1.5">
                <h2 className="text-lg font-bold text-[#131b2e] font-headline leading-tight">Infrio</h2>
                {envLabel && <span className={styles.envBadge} data-env={envLabel}>{envLabel}</span>}
              </div>
              <p className="text-[10px] uppercase tracking-widest text-on-surface-variant opacity-60">
                {context === 'dashboard' ? (user?.planName ?? 'Global Account') : 'Project Context'}
              </p>
              <p className={styles.version} title={`build ${__APP_BUILD__}`}>v{__APP_VERSION__}</p>
            </div>
          </div>
        </div>

        <nav className={styles.nav}>
          {context === 'project' && (
            <>
              <NavLink to="/projects" className={styles.navLinkBack} onClick={onClose}>
                <span className="material-symbols-outlined">arrow_back</span>
                All Projects
              </NavLink>
              <div className={styles.navDivider} />
            </>
          )}
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === `/projects/${projectId}` || item.to === '/dashboard'}
              className={({ isActive }) =>
                isActive ? `${styles.navLink} ${styles.navLinkActive}` : styles.navLink
              }
              onClick={onClose}
            >
              <span className="material-symbols-outlined">{item.icon}</span>
              {item.label}
            </NavLink>
          ))}

          {isStaff && context === 'dashboard' && (
            <>
              <div className="mt-4 mb-1 px-3">
                <p className="text-[10px] uppercase tracking-widest text-on-surface-variant opacity-50 font-semibold">Admin</p>
              </div>
              {adminNavItems.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end
                  className={({ isActive }) =>
                    isActive ? `${styles.navLink} ${styles.navLinkActive}` : styles.navLink
                  }
                  onClick={onClose}
                >
                  <span className="material-symbols-outlined">{item.icon}</span>
                  {item.label}
                </NavLink>
              ))}
            </>
          )}
        </nav>

        <div className={styles.bottomSection}>
          {context === 'dashboard' && (
            <button
              onClick={() => {
                onClose?.();
                navigate('/projects', { state: { openCreate: true } });
              }}
              className="w-full mb-4 btn-primary py-2.5 rounded-lg text-sm"
            >
              <span className="material-symbols-outlined text-sm">add</span>
              Create New Project
            </button>
          )}

          {user && (
            <div className={styles.userCard}>
              <div className="flex items-center gap-3 mb-3">
                {user.avatar ? (
                  <img src={user.avatar} alt={user.firstName} className="w-8 h-8 rounded-full border border-primary/20 object-cover" />
                ) : (
                  <div className="w-8 h-8 rounded-full bg-primary-container flex items-center justify-center text-white text-xs font-bold">
                    {user.firstName[0]}{user.lastName[0]}
                  </div>
                )}
                <div className="overflow-hidden">
                  <p className="text-xs font-bold truncate">{user.firstName} {user.lastName}</p>
                  <p className="text-[10px] opacity-60 truncate">
                    {user.isSubuser && user.account ? `Team of ${user.account.firstName} ${user.account.lastName}` : roleLabel(user.role)}
                  </p>
                </div>
              </div>
              <button
                onClick={handleLogout}
                className="w-full py-2 bg-primary text-on-primary text-xs font-bold rounded-lg hover:opacity-90 transition-opacity flex items-center justify-center gap-1"
              >
                <span className="material-symbols-outlined text-sm">logout</span>
                Sign Out
              </button>
            </div>
          )}
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
